import type { UtilityConflictGroup } from '@/types/broadcast';
import type {
    MultiTemplateResolutionResult,
    MultiTemplateSelectionEntry,
} from '../types';

/**
 * Deriva uma key estável para um grupo de conflito a partir do conjunto de
 * page_ids envolvidas — o backend não retorna um id próprio pro conflito,
 * então usamos o conjunto ordenado de páginas como identidade. Estável entre
 * chamadas de overlap-check desde que o conjunto de páginas do conflito não mude
 * (se mudar, é um conflito diferente e a resolução manual antiga não se aplica).
 */
export function conflictGroupKey(conflict: UtilityConflictGroup): string {
    return [...conflict.page_ids].sort().join('|');
}

/**
 * O auxiliar de uma coluna também é um utility template real, aprovado por um
 * subconjunto de páginas — pode conflitar com o principal ou o auxiliar de
 * outras colunas. Mas "vencer" um conflito sempre significa a COLUNA inteira
 * (principal + seu auxiliar) ganhar aquela página, nunca um template auxiliar
 * sozinho sem o principal que o acompanha. Por isso todo group_id (principal
 * OU auxiliar) é remapeado para o group_id do PRINCIPAL da coluna dona antes
 * de calcular/exibir/resolver conflitos.
 */
export function columnGroupIdForGroup(
    groupId: number,
    selection: MultiTemplateSelectionEntry[]
): number | null {
    for (const entry of selection) {
        if (entry.groupId === groupId || entry.secondaryGroupId === groupId) {
            return entry.groupId;
        }
    }
    return null;
}

/**
 * Todos os group_ids envolvidos na esteira — principais + auxiliares
 * escolhidos — usados para o overlap-check unificado (o auxiliar participa do
 * mesmo cálculo de sobreposição que o principal).
 */
export function allInvolvedGroupIds(selection: MultiTemplateSelectionEntry[]): number[] {
    const ids = new Set<number>();
    for (const entry of selection) {
        ids.add(entry.groupId);
        if (entry.secondaryGroupId != null) ids.add(entry.secondaryGroupId);
    }
    return Array.from(ids);
}

/**
 * Para um grupo de conflito, retorna o group_id da COLUNA (principal) de maior
 * prioridade dentre as colunas conflitantes — remapeando cada
 * conflicting_group_id (que pode ser um auxiliar) para o principal da coluna
 * dona antes de comparar prioridade.
 */
export function highestPrioritySelection(
    conflict: UtilityConflictGroup,
    selection: MultiTemplateSelectionEntry[]
): number | null {
    const priorityByGroup = new Map(selection.map(s => [s.groupId, s.priority]));
    let best: { groupId: number; priority: number } | null = null;
    for (const rawGroupId of conflict.conflicting_group_ids) {
        const columnGroupId = columnGroupIdForGroup(rawGroupId, selection);
        if (columnGroupId === null) continue;
        const priority = priorityByGroup.get(columnGroupId);
        if (priority === undefined) continue;
        if (!best || priority < best.priority) {
            best = { groupId: columnGroupId, priority };
        }
    }
    return best?.groupId ?? null;
}

/**
 * Aplica auto-resolver a todos os conflitos pendentes (sem sobrescrever resoluções
 * manuais já existentes) — retorna o novo mapa de resoluções.
 */
export function autoResolveConflicts(
    conflictGroups: UtilityConflictGroup[],
    selection: MultiTemplateSelectionEntry[],
    existingResolutions: Record<string, number>
): Record<string, number> {
    const next = { ...existingResolutions };
    for (const conflict of conflictGroups) {
        const key = conflictGroupKey(conflict);
        if (next[key] !== undefined) continue; // não sobrescreve escolha manual
        const winner = highestPrioritySelection(conflict, selection);
        if (winner !== null) next[key] = winner;
    }
    return next;
}

/**
 * Monta o payload final utility_template_resolution a partir de:
 * - exclusive_by_group (páginas sem conflito, já resolvidas)
 * - conflict_groups + resoluções manuais/automáticas escolhidas pelo usuário
 *
 * Retorna também se ainda há conflitos pendentes (bloqueia confirmação) e a
 * lista de páginas sem cobertura (informativa, não bloqueante).
 */
export function buildUtilityTemplateResolution(params: {
    conflictGroups: UtilityConflictGroup[];
    exclusiveByGroup: Record<string, string[]>;
    uncoveredPageIds: string[];
    conflictResolutions: Record<string, number>;
    /**
     * Revisão v3: quando informado, cada entrada do resultado ganha o
     * `secondary_group_id` vinculado especificamente àquele template principal
     * (lookup por `groupId` -> `secondaryGroupId` na seleção do builder).
     */
    selection?: MultiTemplateSelectionEntry[];
}): MultiTemplateResolutionResult {
    const { conflictGroups, exclusiveByGroup, uncoveredPageIds, conflictResolutions, selection } = params;
    const secondaryByGroup = new Map(
        (selection ?? []).map(s => [s.groupId, s.secondaryGroupId ?? null])
    );

    const pagesByGroup = new Map<number, Set<string>>();
    const addPages = (groupId: number, pageIds: string[]) => {
        const set = pagesByGroup.get(groupId) ?? new Set<string>();
        for (const id of pageIds) set.add(id);
        pagesByGroup.set(groupId, set);
    };

    // exclusive_by_group vem do backend indexado pelo group_id CRU do
    // overlap-check, que agora inclui auxiliares — uma página exclusiva de um
    // group_id que é auxiliar de alguma coluna precisa ser atribuída ao
    // principal daquela coluna (vencer = coluna inteira, nunca o auxiliar sozinho).
    for (const [groupIdStr, pageIds] of Object.entries(exclusiveByGroup)) {
        const rawGroupId = Number(groupIdStr);
        const columnGroupId = selection ? columnGroupIdForGroup(rawGroupId, selection) : rawGroupId;
        addPages(columnGroupId ?? rawGroupId, pageIds);
    }

    let hasPendingConflicts = false;
    for (const conflict of conflictGroups) {
        const key = conflictGroupKey(conflict);
        const winner = conflictResolutions[key];
        if (winner === undefined) {
            hasPendingConflicts = true;
            continue;
        }
        addPages(winner, conflict.page_ids);
    }

    const resolution = Array.from(pagesByGroup.entries())
        .filter(([, pages]) => pages.size > 0)
        .map(([groupId, pages]) => {
            const secondaryGroupId = secondaryByGroup.get(groupId);
            return {
                group_id: groupId,
                page_ids: Array.from(pages),
                ...(secondaryGroupId != null ? { secondary_group_id: secondaryGroupId } : {}),
            };
        });

    return { resolution, uncoveredPageIds, hasPendingConflicts };
}
